import { useCallback, useEffect, useRef, useState } from 'react'
import type { CardManagementCardDetailsResponse } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { fetchCardManagementCardDetails } from 'src/queries/physicalCardManagement/physicalCardManagement.network'

import { CardHeroVariant } from './cardDetailHeroConstants'
import type { CardDetailsAvailability } from './cardDetailHeroMapping'

/** Auto-close so revealed credentials never sit exposed indefinitely. */
const AUTO_CLOSE_MS = 15000

export type CardRevealState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'unavailable'

/** Feature shape isolated from the provisional EX-40580 wire field names. */
export type CardRevealDetails = {
  pan: string
  expiry: string
  cvc: string
  cardholderName: string
  address: CardManagementCardDetailsResponse['billingAddress']
}

type CardRevealDisclosure = {
  /** The variant that currently owns the open reveal, or `null` when closed. */
  anchor: CardHeroVariant | null
  /** True when any variant's reveal is open. */
  open: boolean
  /** Secrets exist only while this is `ready`; every close clears `details`. */
  state: CardRevealState
  details: CardRevealDetails | undefined
  /** True only for the variant that currently owns the reveal. */
  isOpenFor: (variant: CardHeroVariant) => boolean
  /**
   * Toggle the reveal for a variant: open it (moving the anchor to it), or — if
   * it is already the open variant — close it.
   */
  toggle: (variant: CardHeroVariant) => void
  close: () => void
  /**
   * The outside-click / Escape container ref for a variant. Put it on the element
   * wrapping BOTH that variant's Show-details trigger and its reveal panel.
   */
  containerRefFor: (variant: CardHeroVariant) => React.RefObject<HTMLDivElement>
}

type CardRevealDisclosureOptions = {
  creditCardId: string | undefined
  /**
   * EX-40596 source selector, shared with the Available credit region. When
   * false the reveal resolves from `demoDetails` synchronously — no request, no
   * capability check — so the interaction still works before EX-40580 exists.
   * When true it takes the live path below.
   */
  cardHeaderLiveDataEnabled: boolean
  /**
   * Demo credentials for the non-live path. `undefined` only while the identity
   * query is still resolving, since the real cardholder name and expiry are
   * paired with the demo PAN/CVC.
   */
  demoDetails: CardRevealDetails | undefined
  cardDetailsAvailability: CardDetailsAvailability | undefined
  cardDetailsCapabilityError: boolean
  /** Plain page-owned route signal; the feature remains router-free. */
  navigationKey: string
  autoCloseMs?: number
}

/**
 * Non-modal disclosure state for the card-details reveal, owned by the hero
 * shell so the full hero and the pinned compact bar share one source of truth.
 * Both heroes stay mounted, so the reveal is *anchored* to whichever one opened
 * it: only that variant renders the panel and reports itself open. It is a
 * disclosure, not a modal — the page stays interactive and focus is not trapped.
 * Escape and a genuine outside click close it, plus an auto-close timeout so
 * sensitive values are not left on screen.
 */
export const useCardRevealDisclosure = ({
  creditCardId,
  cardHeaderLiveDataEnabled,
  demoDetails,
  cardDetailsAvailability,
  cardDetailsCapabilityError,
  navigationKey,
  autoCloseMs = AUTO_CLOSE_MS,
}: CardRevealDisclosureOptions): CardRevealDisclosure => {
  const [anchor, setAnchor] = useState<CardHeroVariant | null>(null)
  const [state, setState] = useState<CardRevealState>('idle')
  const [details, setDetails] = useState<CardRevealDetails>()
  const fullRef = useRef<HTMLDivElement>(null)
  const compactRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<CardHeroVariant | null>(null)
  const requestGenerationRef = useRef(0)
  const abortControllerRef = useRef<AbortController>()

  const cancelRequest = useCallback((): void => {
    requestGenerationRef.current += 1
    abortControllerRef.current?.abort()
    abortControllerRef.current = undefined
  }, [])

  /** Every close path destroys the only state slot that can hold PAN or CVC. */
  const close = useCallback((): void => {
    cancelRequest()
    anchorRef.current = null
    setAnchor(null)
    setDetails(undefined)
    setState('idle')
  }, [cancelRequest])

  const toggle = useCallback(
    (variant: CardHeroVariant): void => {
      if (anchorRef.current === variant) {
        close()
        return
      }

      cancelRequest()
      anchorRef.current = variant
      setAnchor(variant)
      setDetails(undefined)

      // Demo path: resolve from the constants, synchronously. No request is made
      // and no capability is consulted, because neither exists to consult —
      // that is precisely why this path is the default.
      if (!cardHeaderLiveDataEnabled) {
        if (demoDetails === undefined) {
          setState('loading')
          return
        }
        setDetails(demoDetails)
        setState('ready')
        return
      }

      const canReveal =
        cardDetailsAvailability === 'AVAILABLE' ||
        cardDetailsAvailability === 'READ_ONLY'
      if (!creditCardId || cardDetailsCapabilityError || !canReveal) {
        setState('unavailable')
        return
      }

      setState('loading')
      const requestGeneration = requestGenerationRef.current
      const abortController = new AbortController()
      abortControllerRef.current = abortController

      // Deliberately imperative: PAN/CVC must never enter the React Query cache.
      // The event that opens the disclosure owns this one request.
      fetchCardManagementCardDetails(creditCardId, abortController.signal).then(
        (response) => {
          if (requestGenerationRef.current !== requestGeneration) return
          abortControllerRef.current = undefined
          setDetails({
            pan: response.pan,
            expiry: response.expiry,
            cvc: response.cvc,
            cardholderName: response.billingName,
            address: response.billingAddress,
          })
          setState('ready')
        },
        () => {
          // Do not retain, stringify, log, or report the raw error: an HTTP
          // client error can carry a response body containing sensitive data.
          if (requestGenerationRef.current !== requestGeneration) return
          abortControllerRef.current = undefined
          setDetails(undefined)
          setState('error')
        },
      )
    },
    [
      cancelRequest,
      cardDetailsAvailability,
      cardDetailsCapabilityError,
      close,
      creditCardId,
      demoDetails,
      cardHeaderLiveDataEnabled,
    ],
  )

  const containerRefFor = useCallback(
    (variant: CardHeroVariant): React.RefObject<HTMLDivElement> =>
      variant === 'full' ? fullRef : compactRef,
    [],
  )

  // Resolve a demo reveal that was opened before identity arrived. `toggle` reads
  // `demoDetails` once, at click time, so a click landing while the card query is
  // still in flight parks the panel on `loading` with nothing to move it on — the
  // auto-close would be its only exit. Reachable in ordinary use: the demo path
  // never gates the trigger, and the toolbar renders before `card` resolves.
  //
  // Gated on `loading` so this settles exactly once. The shell rebuilds
  // `demoDetails` on every render, so an ungated effect would re-run on each one.
  useEffect(() => {
    if (cardHeaderLiveDataEnabled) return
    if (anchor === null || state !== 'loading' || demoDetails === undefined) {
      return
    }
    setDetails(demoDetails)
    setState('ready')
  }, [anchor, cardHeaderLiveDataEnabled, demoDetails, state])

  // Auto-close so revealed values never sit exposed indefinitely.
  //
  // Keyed on `ready`, not on `anchor`. The anchor is set synchronously by
  // `toggle`, but nothing is on screen until the values resolve — a fetch on the
  // live path, identity on the demo path — so keying on it would measure the
  // window from the click and leave actual exposure at `autoCloseMs − latency`.
  // A slow resolve would then flash the panel and close it, which reads as a bug
  // rather than as a security timeout. Keyed on `ready`, the window always means
  // what the constant says.
  //
  // Deliberately no timer for `loading`, `error`, or `unavailable`: none of them
  // put a value on screen, and all of them are still dismissed by every ordinary
  // path (toggle, Escape, outside click, navigation, unmount).
  useEffect(() => {
    if (state !== 'ready') return undefined
    const timer = setTimeout(close, autoCloseMs)
    return () => clearTimeout(timer)
  }, [state, autoCloseMs, close])

  // The account shell intentionally remains mounted across nested routes. A
  // page-owned navigation signal therefore clears secrets even without unmount.
  useEffect(() => close(), [close, creditCardId, navigationKey])

  // A live capability downgrade clears data immediately while leaving the
  // disclosure open long enough to render an unavailable state.
  //
  // Guarded on the live path: the demo reveal does not consult capabilities, so
  // a verdict about an endpoint it never calls must not close it. UNKNOWN counts
  // as a downgrade here — with EX-40580 unshipped the server names no verdict at
  // all, and on the live path that is indistinguishable to the user from a
  // denial, so it reads the same rather than silently removing the affordance.
  useEffect(() => {
    if (
      anchorRef.current !== null &&
      cardHeaderLiveDataEnabled &&
      (cardDetailsCapabilityError ||
        cardDetailsAvailability === 'UNAVAILABLE' ||
        cardDetailsAvailability === 'UNKNOWN')
    ) {
      cancelRequest()
      setDetails(undefined)
      setState('unavailable')
    }
  }, [
    cancelRequest,
    cardDetailsAvailability,
    cardDetailsCapabilityError,
    cardHeaderLiveDataEnabled,
  ])

  // On unmount, abort and invalidate any late completion. Component state is
  // destroyed by React; importantly, no secret is mirrored into a ref.
  useEffect(
    () => () => {
      requestGenerationRef.current += 1
      abortControllerRef.current?.abort()
      abortControllerRef.current = undefined
    },
    [],
  )

  // Escape + outside-click dismissal, attached only while open. The listener is
  // added after the opening click has finished propagating, so it never
  // self-closes on the same click that opened it.
  //
  // A click is "outside" only when it lands outside BOTH hero containers: while
  // one hero owns the reveal the other is inert, but interacting with either
  // hero must never dismiss the panel (the prototype special-cases this so the
  // swap partner can't close an open reveal). Clicks on the page beyond both
  // heroes close it.
  useEffect(() => {
    if (anchor === null) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') close()
    }
    const onDocumentClick = (event: MouseEvent): void => {
      const target = event.target as Node
      const insideAHero =
        (fullRef.current?.contains(target) ?? false) ||
        (compactRef.current?.contains(target) ?? false)
      if (!insideAHero) close()
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onDocumentClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [anchor, close])

  return {
    anchor,
    open: anchor !== null,
    state,
    details,
    isOpenFor: (variant) => anchor === variant,
    toggle,
    close,
    containerRefFor,
  }
}
